import { Suspense } from "react";
import { lazyWithReload } from "@/utils/lazyWithReload";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";

// Eager: first paint (landing page) and the 404 fallback shouldn't need a spinner.
import LandingPage from "./pages/LandingPage";
import NotFound from "./pages/NotFound";

const LoginPage = lazyWithReload(() => import("./pages/auth/Login"));
const SignUpPage = lazyWithReload(() => import("./pages/auth/SignUp"));
const ForgotPasswordPage = lazyWithReload(() => import("./pages/auth/ForgotPassword"));
const ResetPasswordPage = lazyWithReload(() => import("./pages/auth/ResetPassword"));
const CandidateSignUp = lazyWithReload(() => import("./pages/auth/candidate/CandidateSignUp"));
const CompanySignUp = lazyWithReload(() => import("./pages/auth/company/CompanySignUp"));
const PlansPage = lazyWithReload(() => import("./pages/Plans"));
const ForCompanies = lazyWithReload(() => import("./pages/ForCompanies"));
const CompanyPublicProfile = lazyWithReload(() => import("./pages/CompanyPublicProfile"));
const CareerGuide = lazyWithReload(() => import("./pages/CareerGuide"));
const ContactPage = lazyWithReload(() => import("./pages/Contact"));
const PrivacyPolicyPage = lazyWithReload(() => import("./pages/PrivacyPolicy"));
const TermsOfServicePage = lazyWithReload(() => import("./pages/TermsOfService"));
const AboutUsPage = lazyWithReload(() => import("./pages/AboutUs"));
const FAQPage = lazyWithReload(() => import("./pages/FAQ"));
const SupportPage = lazyWithReload(() => import("./pages/Support"));
const SalesPage = lazyWithReload(() => import("./pages/SalesPage"));
const CompanyEvaluationPage = lazyWithReload(() => import("./pages/CompanyEvaluationPage"));
const CompanyHall = lazyWithReload(() => import("./pages/CompanyHall"));
const BlogPage = lazyWithReload(() => import("./pages/BlogPage"));
const BlogPostPage = lazyWithReload(() => import("./pages/BlogPostPage"));
const AdminBlog = lazyWithReload(() => import("./pages/admin/AdminBlog"));
const ContentIACentral = lazyWithReload(() => import("./pages/admin/ContentIACentral"));
const AdminLogin = lazyWithReload(() => import("./pages/admin/AdminLogin"));
const PublicJobDetail = lazyWithReload(() => import("./pages/PublicJobDetail"));
import FacebookPixel from "./components/FacebookPixel";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { AppErrorBoundary } from "./components/AppErrorBoundary";

// Company Dashboard
const CompanyDashboardLayout = lazyWithReload(() => import("./pages/dashboard/company/CompanyDashboard"));
const CompanyDashboard = lazyWithReload(() => import("./pages/dashboard/company/Dashboard"));
const MyJobs = lazyWithReload(() => import("./pages/dashboard/company/MyJobs"));
const CreateJob = lazyWithReload(() => import("./pages/dashboard/company/CreateJob"));
const EditJob = lazyWithReload(() => import("./pages/dashboard/company/EditJob"));
const Applicants = lazyWithReload(() => import("./pages/dashboard/company/Applicants"));
const TalentPool = lazyWithReload(() => import("./pages/dashboard/company/TalentPool"));
const CompanyPlans = lazyWithReload(() => import("./pages/dashboard/company/CompanyPlans"));
const CompanySettings = lazyWithReload(() => import("./pages/dashboard/company/Settings"));
const CompanyChatPage = lazyWithReload(() => import("./pages/dashboard/company/Chat"));

// Candidate Dashboard
const CandidateDashboardLayout = lazyWithReload(() => import("./pages/dashboard/candidate/CandidateDashboard"));
const CandidateDashboard = lazyWithReload(() => import("./pages/dashboard/candidate/Dashboard"));
const MyApplications = lazyWithReload(() => import("./pages/dashboard/candidate/MyApplications"));
const SearchJobs = lazyWithReload(() => import("./pages/dashboard/candidate/SearchJobs"));
const Profile = lazyWithReload(() => import("./pages/dashboard/candidate/Profile"));
const ChatPage = lazyWithReload(() => import("./pages/dashboard/candidate/Chat"));
const CandidateReferralPage = lazyWithReload(() => import("./pages/dashboard/candidate/CandidateReferralPage"));
const JobDetail = lazyWithReload(() => import("./pages/dashboard/candidate/JobDetail"));
const JobTest = lazyWithReload(() => import("./pages/dashboard/candidate/JobTest"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

// The Router (BrowserRouter for the real app, MemoryRouter for the build-time
// prerender script) wraps <App /> from the outside - see main.tsx and
// entry-prerender.tsx - so both entry points render the exact same tree
// below this point and hydration doesn't see a structural mismatch.
const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
        <FacebookPixel />
        <CookieConsentBanner />
        <AuthProvider>
          <AppErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/cadastro" element={<SignUpPage />} />
            <Route path="/cadastro/candidato" element={<CandidateSignUp />} />
            <Route path="/cadastro/empresa" element={<CompanySignUp />} />
            <Route path="/planos" element={<PlansPage />} />
            <Route path="/vendas" element={<SalesPage />} />
            <Route path="/para-empresas" element={<ForCompanies />} />
            <Route path="/empresa/perfil/:companyId" element={<CompanyPublicProfile />} />
            <Route path="/empresa/:companyId/avaliar" element={<CompanyEvaluationPage />} />
            <Route path="/empresas" element={<CompanyHall />} />
            <Route path="/guia-de-cargos" element={<CareerGuide />} />
            <Route path="/contato" element={<ContactPage />} />
            <Route path="/privacidade" element={<PrivacyPolicyPage />} />
            <Route path="/termos" element={<TermsOfServicePage />} />
            <Route path="/sobre-nos" element={<AboutUsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/suporte" element={<SupportPage />} />
            <Route path="/trabalhe-conosco" element={<ContactPage />} />
            <Route path="/vagas/:jobId" element={<PublicJobDetail />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />


            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route element={<AdminRoute />}>
              <Route path="/admin/blog" element={<AdminBlog />} />
              <Route path="/admin/content-ia" element={<ContentIACentral />} />
            </Route>

            {/* Company Dashboard Routes */}
            <Route element={<ProtectedRoute allowedRoles={['company']} />}>
              <Route path="/empresa" element={<CompanyDashboardLayout />}>
                <Route index element={<CompanyDashboard />} />
                <Route path="dashboard" element={<CompanyDashboard />} />
                <Route path="vagas" element={<MyJobs />} />
                <Route path="vagas/criar" element={<CreateJob />} />
                <Route path="vagas/editar/:jobId" element={<EditJob />} />
                <Route path="candidatos" element={<Applicants />} />
                <Route path="talentos" element={<TalentPool />} />
                <Route path="plano" element={<CompanyPlans />} />
                <Route path="configuracoes" element={<CompanySettings />} />
                <Route path="chat" element={<CompanyChatPage />} />
              </Route>
            </Route>

            {/* Candidate Dashboard Routes */}
            <Route element={<ProtectedRoute allowedRoles={['candidate']} />}>
              <Route path="/candidato" element={<CandidateDashboardLayout />}>
                <Route index element={<CandidateDashboard />} />
                <Route path="dashboard" element={<CandidateDashboard />} />
                <Route path="candidaturas" element={<MyApplications />} />
                <Route path="vagas" element={<SearchJobs />} />
                <Route path="vagas/:jobId" element={<JobDetail />} />
                <Route path="vagas/:jobId/teste" element={<JobTest />} />
                <Route path="perfil" element={<Profile />} />
                <Route path="chat" element={<ChatPage />} />
                <Route path="indicar" element={<CandidateReferralPage />} /> {/* Nova Rota */}
              </Route>
            </Route>

            {/* Catch-all Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </AppErrorBoundary>
        </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;